import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const COUNTRY_CODES = [
  { code: '+55', label: 'Brasil', areaLabel: 'DDD' },
  { code: '+1', label: 'EUA/Canadá', areaLabel: 'Área' },
  { code: '+351', label: 'Portugal', areaLabel: 'Área' },
  { code: '+34', label: 'Espanha', areaLabel: 'Área' },
  { code: '+595', label: 'Paraguai', areaLabel: 'Área' },
  { code: '+54', label: 'Argentina', areaLabel: 'Área' },
];

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

export const composePhone = ({ countryCode = '+55', areaCode = '', number = '' }) => {
  const country = digitsOnly(countryCode);
  const area = digitsOnly(areaCode);
  const local = digitsOnly(number);
  if (!country || (!area && !local)) return '';
  return `+${country}${area}${local}`;
};

export const splitPhone = (value) => {
  const digits = digitsOnly(value);
  const country = COUNTRY_CODES.find((item) => digits.startsWith(digitsOnly(item.code))) || COUNTRY_CODES[0];
  const countryDigits = digitsOnly(country.code);
  const rest = digits.startsWith(countryDigits) ? digits.slice(countryDigits.length) : digits;
  const areaLength = country.code === '+55' ? 2 : Math.min(3, Math.max(0, rest.length - 7));
  return {
    countryCode: country.code,
    areaCode: rest.slice(0, areaLength),
    number: rest.slice(areaLength),
  };
};

export const isValidPhone = (value) => {
  const digits = digitsOnly(value);
  return digits.length >= 10 && digits.length <= 15;
};

export const compactPhoneList = (phones = []) => (
  Array.isArray(phones) ? phones.filter((phone) => digitsOnly(phone).length > 0) : []
);

export const validPhoneList = (phones = []) => compactPhoneList(phones).filter(isValidPhone);

const PhoneNumberInput = ({ value, onChange, disabled = false, idPrefix = 'phone', compact = false }) => {
  const parsed = useMemo(() => splitPhone(value), [value]);
  const [local, setLocal] = useState(parsed);

  useEffect(() => {
    setLocal(parsed);
  }, [parsed]);

  const emit = (next) => {
    setLocal(next);
    onChange?.(composePhone(next));
  };

  const selectedCountry = COUNTRY_CODES.find((item) => item.code === local.countryCode) || COUNTRY_CODES[0];
  const gridClass = compact
    ? 'grid grid-cols-[minmax(96px,1fr)_72px] gap-2'
    : 'grid grid-cols-1 sm:grid-cols-[132px_80px_minmax(180px,1fr)] gap-2';
  const inputClass = compact ? 'h-8 min-w-0' : 'min-w-0';

  return (
    <div className={gridClass}>
      <div className="space-y-1 min-w-0">
        <Label htmlFor={`${idPrefix}-country`} className="text-xs text-muted-foreground">País</Label>
        <Select
          value={local.countryCode}
          onValueChange={(countryCode) => emit({ ...local, countryCode })}
          disabled={disabled}
        >
          <SelectTrigger id={`${idPrefix}-country`} className={inputClass}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_CODES.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                {country.code} {compact ? '' : country.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1 min-w-0">
        <Label htmlFor={`${idPrefix}-area`} className="text-xs text-muted-foreground">{selectedCountry.areaLabel}</Label>
        <Input
          id={`${idPrefix}-area`}
          value={local.areaCode}
          onChange={(e) => emit({ ...local, areaCode: digitsOnly(e.target.value).slice(0, 4) })}
          disabled={disabled}
          inputMode="numeric"
          className={inputClass}
          placeholder="11"
        />
      </div>
      <div className={compact ? 'space-y-1 min-w-0 col-span-2' : 'space-y-1 min-w-0'}>
        <Label htmlFor={`${idPrefix}-number`} className="text-xs text-muted-foreground">Número</Label>
        <Input
          id={`${idPrefix}-number`}
          value={local.number}
          onChange={(e) => emit({ ...local, number: digitsOnly(e.target.value).slice(0, 10) })}
          disabled={disabled}
          inputMode="numeric"
          className={inputClass}
          placeholder="999999999"
        />
      </div>
    </div>
  );
};

export const PhoneListInput = ({ value = [], onChange, disabled = false, idPrefix = 'phones', compact = false }) => {
  const phones = Array.isArray(value) && value.length > 0 ? value : [''];

  const updatePhone = (index, nextPhone) => {
    const next = phones.map((phone, idx) => (idx === index ? nextPhone : phone));
    onChange?.(next);
  };

  const removePhone = (index) => {
    const next = phones.filter((_, idx) => idx !== index);
    onChange?.(next.length > 0 ? next : ['']);
  };

  return (
    <div className="space-y-2">
      {phones.map((phone, index) => (
        <div key={`${idPrefix}-${index}`} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-end">
          <PhoneNumberInput
            value={phone}
            onChange={(nextPhone) => updatePhone(index, nextPhone)}
            disabled={disabled}
            idPrefix={`${idPrefix}-${index}`}
            compact={compact}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removePhone(index)}
            disabled={disabled || phones.length === 1}
            className={compact ? 'h-8 w-8' : undefined}
            aria-label="Remover telefone"
            title="Remover telefone"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => onChange?.([...phones, ''])}
        disabled={disabled}
      >
        <Plus className="w-4 h-4" />
        Adicionar telefone
      </Button>
    </div>
  );
};

export default PhoneNumberInput;
